import React from 'react';
import { Routes, Route, useParams, Navigate } from 'react-router-dom';
import { Box, Container, Typography, Paper } from '@mui/material';
import { FormList } from '../components/form-list';
import { FormBuilder } from '../components/form-builder';
import { FormRenderer } from '../components/form-renderer';
import { ResponsesList, ResponseDetail, FormResponsesList } from '../pages/responses';
import { EmbedThemeLayout, FormThemeLayout } from '../theme';


export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<EmbedThemeLayout />}>
        <Route path="/tenant/:tenantId/forms" element={<TenantForms />} />
        <Route path="/tenant/:tenantId/form/create" element={<CreateFormRedirect />} />
        <Route
          path="/tenant/:tenantId/responses"
          element={<ResponsesListRoute />}
        />
        <Route
          path="/tenant/:tenantId/form-response/:responseId"
          element={<ResponseDetailRoute />}
        />
        <Route
          path="/tenant/:tenantId/form/:formId/responses"
          element={<FormResponsesListRoute />}
        />
        <Route path="/" element={<HomePage />} />
        <Route path="/tenant/:tenantId/form/new" element={<FormBuilderRoute />} />
        <Route
          path="/tenant/:tenantId/form/:formId/edit"
          element={<FormBuilderRoute />}
        />
      </Route>
      <Route element={<FormThemeLayout />}>
        <Route
          path="/tenant/:tenantId/form/:formId/view"
          element={<FormRendererRoute />}
        />
      </Route>
    </Routes>
  );
};
const HomePage: React.FC = () => {
  return (
    <Container maxWidth={false} sx={{ px: 0 }}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
          px: 4,
        }}
      >
        <Paper sx={{ p: 6, textAlign: 'center', maxWidth: '600px', width: '100%' }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Form Builder Microservice
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
            Please provide a tenant ID in the URL
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Example: /tenant/your-tenant-id/forms
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

const TenantForms: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  if (!tenantId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID is required</Typography>
        </Box>
      </Container>
    );
  }
  return <FormList tenantId={tenantId} />;
};

const FormBuilderRoute: React.FC = () => {
  const { tenantId, formId } = useParams<{
    tenantId: string;
    formId?: string;
  }>();
  if (!tenantId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID is required</Typography>
        </Box>
      </Container>
    );
  }
  return <FormBuilder tenantId={tenantId} formId={formId} />;
};

const FormRendererRoute: React.FC = () => {
  const { tenantId, formId } = useParams<{
    tenantId: string;
    formId: string;
  }>();
  if (!tenantId || !formId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID and Form ID are required</Typography>
        </Box>
      </Container>
    );
  }
  return <FormRenderer tenantId={tenantId} formId={formId} />;
};

const CreateFormRedirect: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  if (!tenantId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID is required</Typography>
        </Box>
      </Container>
    );
  }
  return <Navigate to={`/tenant/${tenantId}/forms?create=1`} replace />;
};

const ResponsesListRoute: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  if (!tenantId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID is required</Typography>
        </Box>
      </Container>
    );
  }
  return <ResponsesList />;
};

const ResponseDetailRoute: React.FC = () => {
  const { responseId } = useParams<{
    tenantId?: string;
    responseId: string;
  }>();
  if (!responseId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Response ID is required</Typography>
        </Box>
      </Container>
    );
  }
  return <ResponseDetail />;
};

const FormResponsesListRoute: React.FC = () => {
  const { tenantId, formId } = useParams<{
    tenantId: string;
    formId: string;
  }>();
  if (!tenantId || !formId) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 0 }}>
        <Box sx={{ px: 4 }}>
          <Typography color="error">Tenant ID and Form ID are required</Typography>
        </Box>
      </Container>
    );
  }
  return <FormResponsesList />;
};


